import { useCallback, useEffect, useRef, useState } from "react";
import { isOutage, system, type HealthReport } from "@/features/system/api/system.api";
import { isOffline } from "@/lib/apiClient";

export type HealthPhase = "checking" | "up" | "down";

export interface SystemHealth {
  phase: HealthPhase;
  report: HealthReport | null;
  checkedAt: number | null;
  busy: boolean;
  recheck: () => void;
}

const PROBE_TIMEOUT_MS = 8_000;
const UP_INTERVAL_MS = 60_000;
const DOWN_INTERVAL_MS = 15_000;

export function useSystemHealth(): SystemHealth {
  const [phase, setPhase] = useState<HealthPhase>("checking");
  const [report, setReport] = useState<HealthReport | null>(null);
  const [checkedAt, setCheckedAt] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const check = useCallback(async () => {
    if (inFlight.current) return;
    if (isOffline()) {
      setPhase("up");
      setCheckedAt(Date.now());
      return;
    }
    inFlight.current = true;
    setBusy(true);

    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
    try {
      const next = await system.health(controller.signal);
      if (!mounted.current) return;
      setReport(next);
      setPhase(next.status === "maintenance" ? "down" : "up");
    } catch (error) {
      if (!mounted.current) return;
      setReport(null);
      setPhase(isOutage(error) ? "down" : "up");
    } finally {
      window.clearTimeout(timeout);
      inFlight.current = false;
      if (mounted.current) {
        setBusy(false);
        setCheckedAt(Date.now());
      }
    }
  }, []);

  useEffect(() => {
    void check();
  }, [check]);

  useEffect(() => {
    if (phase === "checking") return;
    const delay = phase === "down" ? DOWN_INTERVAL_MS : UP_INTERVAL_MS;
    const id = window.setTimeout(() => void check(), delay);
    return () => window.clearTimeout(id);
  }, [phase, checkedAt, check]);

  useEffect(() => {
    const wake = () => void check();
    window.addEventListener("online", wake);
    window.addEventListener("focus", wake);
    return () => {
      window.removeEventListener("online", wake);
      window.removeEventListener("focus", wake);
    };
  }, [check]);

  const recheck = useCallback(() => void check(), [check]);

  return { phase, report, checkedAt, busy, recheck };
}
